import React, { useState, useEffect, useMemo, useCallback, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  Grid,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Avatar,
  alpha,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Collapse,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Assignment,
  Block as BlockIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  HourglassEmpty as PendingIcon,
  Warning as WarningIcon,
  EventBusy as OverdueIcon,
  CheckCircleOutline as NoInvoiceIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  ExpandMore,
  ExpandLess,
  Build as BuildIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Contract } from '../types';
import { contractService } from '../services/contractService';
import ContratoServicosService from '../services/contratoServicosService';
import ContratosTable from '../components/contract/ContratosTable';
import SysDateRangePicker from '../components/SysDateRangePicker';
import BulkEditContractsDialog from '../components/contract/BulkEditContractsDialog';
import GatewaySelect from '../components/selects/GatewaySelect';
import FuncionariosSelect from '../components/selects/FuncionariosSelect';
import PlanosSelect from '../components/selects/PlanosSelect';
import DiasVencimentoSelect from '../components/selects/DiasVencimentoSelect';
import { usePermissions } from '../contexts/AuthContext';
import { detailPageHeaderPaperSx } from '../styles/theme';

const ContractsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { can } = usePermissions();
  const podeCriar = can('contratos.criar');
  const podeEditar = can('contratos.editar');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  // Estados de paginação e filtros
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [filters, setFilters] = useState<any>({});
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>('all');

  // Estados locais para busca (aplicados)
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [signatureStatus, setSignatureStatus] = useState<string>('all');
  const [advancedFilters, setAdvancedFilters] = useState<any>({});
  const [blockStartDate, setBlockStartDate] = useState('');
  const [blockEndDate, setBlockEndDate] = useState('');
  const [contractStatus, setContractStatus] = useState<string>('all');

  // Estados temporários para campos
  const [tempSearch, setTempSearch] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [tempSignatureStatus, setTempSignatureStatus] = useState<string>('all');
  const [tempBlockStartDate, setTempBlockStartDate] = useState('');
  const [tempBlockEndDate, setTempBlockEndDate] = useState('');
  const [tempContractStatus, setTempContractStatus] = useState<string>('all');
  const [tempAdvancedFilters, setTempAdvancedFilters] = useState<any>({
    dia_vencimento: '',
    fidelidade: '',
    isento: '',
    sms_lembrete: '',
    gerar_fatura_automatico: '',
    geranfcom: '',
    pgcorte: '',
    dias_corte: '',
    acrescimo: '',
    desconto: '',
    gateway_id: '',
    cfop: '',
    modalidade: '',
    vendedor_id: '',
    sem_gateway: false,
    plano_id: '',
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [repairingIds, setRepairingIds] = useState<number[]>([]);
  const [isRepairing, setIsRepairing] = useState(false);

  const queryClient = useQueryClient();

  // ─── Queries ───────────────────────────────────────────────────────────────

  // Buscar todos os contratos (com limite alto para filtro local se necessário, 
  // mas aqui vamos usar o filtro de status do backend e os demais locais)
  const {
    data: contractsResponse,
    isLoading: loadingContracts,
    error: contractsError,
  } = useQuery({
    queryKey: ['contracts-list', filters, advancedFilters],
    queryFn: () => contractService.getAll({
      limit: 1000,
      filters: { ...filters, ...advancedFilters }
    }),
    staleTime: 30_000,
  });

  // Buscar estatísticas
  const {
    data: statsResponse,
    isLoading: loadingStats,
  } = useQuery({
    queryKey: ['contracts-stats-page'],
    queryFn: () => contractService.getStats({}),
    staleTime: 60_000,
  });

  const allContracts = contractsResponse?.data || [];
  const stats = statsResponse?.data;

  // ─── Lógica de Filtro Local ──────────────────────────────────────────────────

  const filteredContracts = useMemo(() => {
    let list = [...allContracts];

    if (search.trim()) {
      const s = search.toLowerCase();
      const matchStr = (val?: string | null) =>
        !!val && String(val).toLowerCase().includes(s);
      const matchServicosOuRaiz = (c: Contract) => {
        if (matchStr(c.usuario_pppoe) || matchStr(c.endereco_ip)) return true;
        const servicos = c.servicos_adicionais;
        if (!Array.isArray(servicos)) return false;
        return servicos.some(
          (sv) => matchStr(sv.usuario_pppoe) || matchStr(sv.endereco_ip)
        );
      };
      list = list.filter(
        (c) =>
          String(c.id).includes(s) ||
          c.titulo?.toLowerCase().includes(s) ||
          c.cliente?.nome?.toLowerCase().includes(s) ||
          c.plano?.nome?.toLowerCase().includes(s) ||
          matchServicosOuRaiz(c)
      );
    }

    if (startDate) {
      list = list.filter(c => new Date(c.data_contrato) >= new Date(startDate));
    }
    if (endDate) {
      list = list.filter(c => new Date(c.data_contrato) <= new Date(endDate + 'T23:59:59'));
    }

    if (signatureStatus !== 'all') {
      list = list.filter(c => {
        const isSigned = !!(c.data_assinatura) && !!(c.assinado_por);
        return signatureStatus === 'assinado' ? isSigned : !isSigned;
      });
    }

    // Filtro por data de bloqueio
    if (blockStartDate) {
      list = list.filter(c => c.data_bloqueio && new Date(c.data_bloqueio) >= new Date(blockStartDate));
    }
    if (blockEndDate) {
      list = list.filter(c => c.data_bloqueio && new Date(c.data_bloqueio) <= new Date(blockEndDate + 'T23:59:59'));
    }

    // Filtro por status do contrato (filtro local adicional)
    if (contractStatus === 'em_observacao') {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      list = list.filter(c => {
        if (!c.desbloqueio_confianca) return false;
        const dataConfianca = new Date(c.desbloqueio_confianca);
        dataConfianca.setHours(0, 0, 0, 0);
        return dataConfianca >= hoje;
      });
    } else if (contractStatus !== 'all') {
      list = list.filter(c => c.status === contractStatus);
    }

    return list;
  }, [allContracts, search, startDate, endDate, signatureStatus, blockStartDate, blockEndDate, contractStatus]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleApplyFilters = () => {
    setSearch(tempSearch);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSignatureStatus(tempSignatureStatus);
    setBlockStartDate(tempBlockStartDate);
    setBlockEndDate(tempBlockEndDate);
    setContractStatus(tempContractStatus);
    setAdvancedFilters({ ...tempAdvancedFilters });
    setPage(0);
    toast.success('Filtros aplicados');
  };

  const handleClearFilters = () => {
    setTempSearch('');
    setTempStartDate('');
    setTempEndDate('');
    setTempSignatureStatus('all');
    setTempBlockStartDate('');
    setTempBlockEndDate('');
    setTempContractStatus('all');
    setTempAdvancedFilters({
      dia_vencimento: '',
      fidelidade: '',
      isento: '',
      sms_lembrete: '',
      gerar_fatura_automatico: '',
      geranfcom: '',
      pgcorte: '',
      dias_corte: '',
      acrescimo: '',
      desconto: '',
      gateway_id: '',
      cfop: '',
      modalidade: '',
      vendedor_id: '',
      sem_gateway: false,
      plano_id: '',
    });
    setSearch('');
    setStartDate('');
    setEndDate('');
    setSignatureStatus('all');
    setBlockStartDate('');
    setBlockEndDate('');
    setContractStatus('all');
    setAdvancedFilters({
      dia_vencimento: '',
      fidelidade: '',
      isento: '',
      sms_lembrete: '',
      gerar_fatura_automatico: '',
      geranfcom: '',
      pgcorte: '',
      dias_corte: '',
      acrescimo: '',
      desconto: '',
      gateway_id: '',
      cfop: '',
      modalidade: '',
      vendedor_id: '',
      sem_gateway: false,
      plano_id: '',
    });
    setPage(0);
    toast.info('Filtros limpos');
  };

  const handleStatusFilter = (status: string) => {
    setActiveStatusFilter(status);
    setPage(0);

    const newFilters: any = {};
    if (status !== 'all') {
      newFilters.status = status;
    }
    setFilters(newFilters);
  };

  const handleBulkRepairConnection = async () => {
    if (selectedIds.length === 0) return;

    const confirmResult = await Swal.fire({
      title: 'Reparar Conexão em Massa',
      text: `Deseja realmente reparar a conexão de ${selectedIds.length} contrato(s)? Isso irá recriar os segredos no RADIUS e MikroTik para todos os serviços de internet ativos/bloqueados.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, Reparar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: theme.palette.primary.main,
      cancelButtonColor: theme.palette.error.main,
    });

    if (!confirmResult.isConfirmed) return;

    setIsRepairing(true);
    let totalServicosReparados = 0;
    let contratosProcessados = 0;

    Swal.fire({
      title: 'Reparando Conexões...',
      html: `Processando <b>0</b> de ${selectedIds.length} contrato(s)...`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      for (const contractId of selectedIds) {
        setRepairingIds(prev => [...prev, contractId]);

        try {
          const servicesRes = await ContratoServicosService.getByContractId(contractId);

          if (servicesRes.success && servicesRes.data) {
            const internetServices = servicesRes.data.filter(s =>
              s.tipo === 'internet' &&
              (s.status === 'ativo' || s.status === 'bloqueado')
            );

            for (const service of internetServices) {
              await ContratoServicosService.repararConexao(service.id);
              totalServicosReparados++;
            }
          }
        } catch (err) {
          console.error(`Erro ao reparar contrato ${contractId}:`, err);
        }

        contratosProcessados++;
        Swal.update({
          html: `Processando <b>${contratosProcessados}</b> de ${selectedIds.length} contrato(s)...`
        });
      }

      await Swal.fire({
        title: 'Concluído!',
        text: `Reparação concluída! ${totalServicosReparados} serviço(s) em ${contratosProcessados} contrato(s) processados.`,
        icon: 'success',
        confirmButtonColor: theme.palette.primary.main,
      });
    } catch (error) {
      console.error('Erro na reparação em massa:', error);
      Swal.fire({
        title: 'Erro!',
        text: 'Ocorreu um erro durante a reparação em massa.',
        icon: 'error',
        confirmButtonColor: theme.palette.primary.main,
      });
    } finally {
      setIsRepairing(false);
      setRepairingIds([]);
      queryClient.invalidateQueries({ queryKey: ['contracts-list'] });
    }
  };

  // ─── Renderização de KPI Cards ──────────────────────────────────────────────

  const renderKpiCard = (
    titulo: string,
    valor: number | string,
    icone: React.ReactElement,
    cor: string,
    statusKey: string,
    sub?: string,
    isGradient = false,
    percentual?: number
  ) => {
    const isActive = activeStatusFilter === statusKey;

    if (isGradient) {
      return (
        <Paper
          elevation={isActive ? 4 : 1}
          onClick={() => handleStatusFilter(statusKey)}
          sx={{
            p: 2,
            borderRadius: 3,
            background: isActive
              ? `linear-gradient(135deg, ${cor} 0%, ${alpha(cor, 0.7)} 100%)`
              : `linear-gradient(135deg, ${alpha(cor, 0.9)} 0%, ${alpha(cor, 0.7)} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            transition: 'all 0.2s',
            transform: isActive ? 'scale(1.02)' : 'none',
            '&:hover': { transform: 'translateY(-3px) scale(1.01)', boxShadow: 6 }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
              {titulo}
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
              {loadingStats ? '...' : valor}
            </Typography>
            {sub && (
              <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600, fontSize: '0.65rem' }}>
                {sub}
              </Typography>
            )}
          </Box>
          {React.cloneElement(icone as any, {
            sx: {
              position: 'absolute',
              right: -10,
              bottom: -10,
              fontSize: 80,
              opacity: 0.15,
              transform: 'rotate(-15deg)'
            }
          })}
        </Paper>
      );
    }

    return (
      <Paper
        elevation={isActive ? 4 : 1}
        onClick={() => handleStatusFilter(statusKey)}
        sx={{
          p: 2,
          borderRadius: 3,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          border: isActive ? `2px solid ${cor}` : `1px solid ${alpha(cor, isDark ? 0.28 : 0.15)}`,
          bgcolor: isActive ? alpha(cor, isDark ? 0.12 : 0.04) : 'background.paper',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: isDark ? `0 8px 24px ${alpha(cor, 0.15)}` : theme.shadows[4],
            bgcolor: alpha(cor, isDark ? 0.1 : 0.04),
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <Avatar
            sx={{
              bgcolor: alpha(cor, 0.1),
              color: cor,
              width: 44,
              height: 44,
              flexShrink: 0,
            }}
          >
            {icone}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>
              {titulo}
            </Typography>
            <Typography variant="h5" fontWeight={800} color="text.primary">
              {loadingStats ? '...' : valor}
            </Typography>
          </Box>
          {percentual !== undefined && !loadingStats && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(cor, 0.08),
                border: `1px solid ${alpha(cor, 0.25)}`,
                borderRadius: 2,
                px: 1,
                py: 0.5,
                minWidth: 52,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{ color: cor, fontSize: '0.85rem', lineHeight: 1.1 }}
              >
                {percentual.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem', lineHeight: 1 }}>
                do total
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  // ─── Estilos e Configurações ───────────────────────────────────────────────

  const cores = {
    primario: theme.palette.primary.main,
    ativo: theme.palette.success.main,
    bloqueado: theme.palette.warning.main,
    cancelado: theme.palette.error.main,
    aguardando: theme.palette.info.main,
    vencido: theme.palette.error.dark,
  };

  return (
    <Box sx={{ p: 0.5, pb: 2, bgcolor: 'background.default', minHeight: '100%' }}>
      <Paper elevation={0} sx={detailPageHeaderPaperSx(theme)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.main', width: 40, height: 40 }}>
              <Assignment sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Gestão de Contratos
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Dashboard e gerenciamento de contratos
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            {selectedIds.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={isRepairing ? <CircularProgress size={18} /> : <BuildIcon sx={{ fontSize: 16 }} />}
                  onClick={handleBulkRepairConnection}
                  disabled={isRepairing}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5 }}
                >
                  {isRepairing ? 'Reparando...' : `Reparar Conexão (${selectedIds.length})`}
                </Button>
                {podeEditar && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setBulkDialogOpen(true)}
                  disabled={isRepairing}
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5 }}
                >
                  Editar em Massa ({selectedIds.length})
                </Button>
                )}
              </>
            )}
            {podeCriar && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/clients')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: theme.shadows[2] },
              }}
            >
              Novo Contrato
            </Button>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Cards de Resumo Estilo Inadimplência */}
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('Total de Contratos', stats?.total || 0, <Assignment />, cores.primario, 'all', 'Quantidade total', true)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard(
            'Contratos Ativos',
            stats?.ativos || 0,
            <CheckIcon />,
            cores.ativo,
            'ativo',
            undefined,
            false,
            stats?.total ? Math.round((stats.ativos / stats.total) * 1000) / 10 : 0
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard(
            'Com Faturas Vencidas',
            stats?.com_faturas_vencidas || 0,
            <OverdueIcon />,
            cores.vencido,
            'com_faturas_vencidas',
            undefined,
            false,
            stats?.total ? Math.round(((stats.com_faturas_vencidas || 0) / stats.total) * 1000) / 10 : 0
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard(
            'Bloqueados',
            stats?.bloqueados || 0,
            <BlockIcon />,
            cores.bloqueado,
            'bloqueado',
            undefined,
            false,
            stats?.total ? Math.round(((stats.bloqueados || 0) / stats.total) * 1000) / 10 : 0
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('Ag. Instalação', stats?.aguardando_instalacao || 0, <PendingIcon />, cores.aguardando, 'aguardando_instalacao')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('Ag. Assinatura', stats?.aguardando_assinatura || 0, <PendingIcon />, theme.palette.text.secondary, 'aguardando_assinatura')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('Sem Faturas', stats?.sem_faturas || 0, <NoInvoiceIcon />, theme.palette.success.main, 'sem_faturas')}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKpiCard('Cancelados', stats?.cancelados || 0, <CancelIcon />, cores.cancelado, 'cancelado')}
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 1, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, ...(isDark && { backgroundImage: 'none' }) }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="ID, título, cliente, plano, usuário PPPoE ou IP..."
              value={tempSearch}
              onChange={(e) => setTempSearch(e.target.value)}
              onKeyUp={(e) => e.key === 'Enter' && handleApplyFilters()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SysDateRangePicker
              label="Data do Contrato"
              onChange={(range) => {
                setTempStartDate(range.startDate ? range.startDate.toISOString().split('T')[0] : '');
                setTempEndDate(range.endDate ? range.endDate.toISOString().split('T')[0] : '');
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SysDateRangePicker
              label="Data de Bloqueio"
              onChange={(range) => {
                setTempBlockStartDate(range.startDate ? range.startDate.toISOString().split('T')[0] : '');
                setTempBlockEndDate(range.endDate ? range.endDate.toISOString().split('T')[0] : '');
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={tempContractStatus}
                label="Status"
                onChange={(e) => setTempContractStatus(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="bloqueado">Bloqueado</MenuItem>
                <MenuItem value="cancelado">Cancelado</MenuItem>
                <MenuItem value="aguardando_instalacao">Ag. Instalação</MenuItem>
                <MenuItem value="aguardando_assinatura">Ag. Assinatura</MenuItem>
                <MenuItem value="suspenso">Suspenso</MenuItem>
                <MenuItem value="em_observacao">Em Observação</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Assinatura</InputLabel>
              <Select
                value={tempSignatureStatus}
                label="Assinatura"
                onChange={(e) => setTempSignatureStatus(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="assinado">Assinados</MenuItem>
                <MenuItem value="nao_assinado">Não Assinados</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleClearFilters} size="small" fullWidth>Limpar</Button>
              <Button variant="contained" onClick={handleApplyFilters} size="small" fullWidth>Filtrar</Button>
              <Tooltip title="Filtros Avançados">
                <IconButton
                  size="small"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  sx={{ bgcolor: showAdvancedFilters ? alpha(theme.palette.primary.main, 0.1) : 'transparent' }}
                >
                  <FilterIcon color={showAdvancedFilters ? 'primary' : 'inherit'} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Filtros Avançados */}
        <Collapse in={showAdvancedFilters}>
          <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <DiasVencimentoSelect
                  value={tempAdvancedFilters.dia_vencimento}
                  onChange={(v) => setTempAdvancedFilters({ ...tempAdvancedFilters, dia_vencimento: v === '' ? '' : String(v) })}
                  allowClear
                  placeholder="Todos"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth size="small" label="Fidelidade" type="number"
                  value={tempAdvancedFilters.fidelidade}
                  onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, fidelidade: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Isento</InputLabel>
                  <Select
                    value={tempAdvancedFilters.isento}
                    label="Isento"
                    onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, isento: e.target.value })}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>SMS Lembrete</InputLabel>
                  <Select
                    value={tempAdvancedFilters.sms_lembrete}
                    label="SMS Lembrete"
                    onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, sms_lembrete: e.target.value })}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Ativo</MenuItem>
                    <MenuItem value="false">Desativado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Fatura Automática</InputLabel>
                  <Select
                    value={tempAdvancedFilters.gerar_fatura_automatico}
                    label="Fatura Automática"
                    onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, gerar_fatura_automatico: e.target.value })}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gerar NFCom</InputLabel>
                  <Select
                    value={tempAdvancedFilters.geranfcom}
                    label="Gerar NFCom"
                    onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, geranfcom: e.target.value })}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Corte Auto (pgcorte)</InputLabel>
                  <Select
                    value={tempAdvancedFilters.pgcorte}
                    label="Corte Auto (pgcorte)"
                    onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, pgcorte: e.target.value })}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="true">Sim</MenuItem>
                    <MenuItem value="false">Não</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth size="small" label="Dias para Corte" type="number"
                  value={tempAdvancedFilters.dias_corte}
                  onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, dias_corte: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth size="small" label="Acréscimo (%)" type="number"
                  value={tempAdvancedFilters.acrescimo}
                  onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, acrescimo: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth size="small" label="Desconto (%)" type="number"
                  value={tempAdvancedFilters.desconto}
                  onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, desconto: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Modalidade</InputLabel>
                  <Select
                    value={tempAdvancedFilters.modalidade}
                    label="Modalidade"
                    onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, modalidade: e.target.value })}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="pos-pago">
                      Pós-pago{' '}
                      <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                        - Paga depois de usar
                      </Box>
                    </MenuItem>
                    <MenuItem value="pre-pago">
                      Pré-pago{' '}
                      <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                        - Paga antes de usar
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth size="small" label="CFOP"
                  value={tempAdvancedFilters.cfop}
                  onChange={(e) => setTempAdvancedFilters({ ...tempAdvancedFilters, cfop: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <GatewaySelect
                      value={tempAdvancedFilters.gateway_id || null}
                      onChange={(val) => setTempAdvancedFilters({ ...tempAdvancedFilters, gateway_id: val })}
                      size="small"
                      disabled={tempAdvancedFilters.sem_gateway}
                    />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={tempAdvancedFilters.sem_gateway}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const checked = e.target.checked;
                          setTempAdvancedFilters({
                            ...tempAdvancedFilters,
                            sem_gateway: checked,
                            gateway_id: checked ? '' : tempAdvancedFilters.gateway_id
                          });
                        }}
                      />
                    }
                    label={<Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Sem gateway</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FuncionariosSelect
                  value={tempAdvancedFilters.vendedor_id || null}
                  onChange={(val) => setTempAdvancedFilters({ ...tempAdvancedFilters, vendedor_id: val })}
                  size="small"
                  label="Vendedor"
                  allowClear
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <PlanosSelect
                  value={tempAdvancedFilters.plano_id ? Number(tempAdvancedFilters.plano_id) : null}
                  onChange={(val) => setTempAdvancedFilters({ ...tempAdvancedFilters, plano_id: val })}
                  size="small"
                  label="Plano"
                  allowClear
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Tabela de Contratos */}
      <ContratosTable
        contratos={filteredContracts}
        loading={loadingContracts}
        error={!!contractsError}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        titulo={
          activeStatusFilter === 'all' ? 'Todos os Contratos' :
            activeStatusFilter === 'ativo' ? 'Contratos Ativos' :
              activeStatusFilter === 'bloqueado' ? 'Contratos Bloqueados' :
                activeStatusFilter === 'cancelado' ? 'Contratos Cancelados' :
                  activeStatusFilter === 'com_faturas_vencidas' ? 'Contratos com Faturas Vencidas' :
                    'Lista de Contratos'
        }
        subtitulo={`${filteredContracts.length} contratos encontrados`}
        headerColor={
          activeStatusFilter === 'ativo' ? cores.ativo :
            activeStatusFilter === 'bloqueado' ? cores.bloqueado :
              activeStatusFilter === 'cancelado' ? cores.cancelado :
                activeStatusFilter === 'com_faturas_vencidas' ? cores.vencido :
                  theme.palette.primary.main
        }
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      {/* Diálogo de Edição em Massa */}
      <BulkEditContractsDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        selectedIds={selectedIds}
        onSuccess={() => {
          setSelectedIds([]);
          queryClient.invalidateQueries({ queryKey: ['contracts-list'] });
          queryClient.invalidateQueries({ queryKey: ['contracts-stats-page'] });
        }}
      />
    </Box>
  );
};

export default ContractsPage;
